import { UserRole } from '../enums/user-role.enum';

/**
 * The roles the Create Staff User screen may hand out.
 *
 * Deliberately not "every UserRole":
 * - guest, visitor, online_user and landlord are not back-office roles — those accounts come
 *   from the public sign-up flows, not from here.
 * - superAdmin is never creatable through the API, and admin only when the caller is a
 *   superAdmin. admin-connect's creatable-role.middleware enforces both; these lists only keep
 *   the form from offering something the server will refuse.
 * - salesPerson is absent on purpose. It exists in this enum but NOT in the authenticator's
 *   UserType, which validates the request with @IsEnum(UserType) — offering it would post a
 *   role the API rejects with a 400.
 */
export const STAFF_CREATABLE_ROLES: UserRole[] = [
  UserRole.COMMERCIAL_MANAGER,
  UserRole.DESIGN_MANAGER,
  UserRole.DESIGN_TEAM,
  UserRole.E_COMMERCE,
  UserRole.ECOMMERCE_SENIOR_SPECIALIST,
  UserRole.ECOMMERCE_SPECIALIST,
  UserRole.FINANCE_MANAGER,
  UserRole.FINANCE_SPECIALIST,
  UserRole.GES,
  UserRole.HOSPITALITY_AGENT,
  UserRole.HOSPITALITY_LEAD,
  UserRole.HOSPITALITY_MANAGER,
  UserRole.HOUSE_KEEPING,
  UserRole.HOUSE_KEEPING_MANAGER,
  UserRole.INVESTOR,
  UserRole.MARKETING_CAMPAIGNS,
  UserRole.MARKETING_SPECIALIST,
  UserRole.OPERATION_MANAGER,
  UserRole.PARTNER_MANAGER,
  UserRole.RESERVATION_AGENT,
  UserRole.RESERVATION_MANAGER,
  UserRole.RESERVATIONS,
  UserRole.SALES_MANAGER,
  UserRole.SALES_SPECIALIST
];

/**
 * Roles a superAdmin may hand out on top of the list above. An admin cannot create another
 * admin — that would let anyone who reaches this screen widen their own blast radius.
 */
export const SUPER_ADMIN_CREATABLE_ROLES: UserRole[] = [UserRole.ADMIN];

/**
 * Every role the Staff Users list covers: all of them except guest and landlord.
 *
 * That pair is the definition of "not staff" here — guests come from the public sign-up flows
 * and have their own list next door, and landlords have their own module at /landlords. Derived
 * from the enum rather than written out, so a role added to UserRole shows up in the staff list
 * and its filter without a second edit.
 *
 * Two entries are worth knowing about: visitor and online_user are not really staff either, and
 * salesPerson has no counterpart in the authenticator's UserType, so filtering by it returns
 * nothing rather than erroring. Both are kept because the rule is "everything except guest and
 * landlord", and carving out more would mean this list silently hides accounts that exist.
 */
export const STAFF_LIST_EXCLUDED_ROLES: UserRole[] = [UserRole.GUEST, UserRole.LANDLORD];

export const STAFF_LIST_ROLES: UserRole[] = Object.values(UserRole).filter(
  (role) => !STAFF_LIST_EXCLUDED_ROLES.includes(role)
);

/**
 * What the role dropdown should offer for the caller currently signed in.
 */
export function getCreatableRoles(callerIsSuperAdmin: boolean): UserRole[] {
  return callerIsSuperAdmin
    ? [...SUPER_ADMIN_CREATABLE_ROLES, ...STAFF_CREATABLE_ROLES]
    : STAFF_CREATABLE_ROLES;
}

/**
 * What a staff account is demoted to when its access is disabled.
 *
 * There is no "disabled" flag on a user, so revoking staff access means moving the account to
 * the ordinary guest role. The account and its history survive; only its access changes.
 */
export const DISABLED_ROLE: UserRole = UserRole.GUEST;

export interface DisableEligibility {
  allowed: boolean;
  /** Why not, phrased for a tooltip. Only set when allowed is false. */
  reason?: string;
}

/**
 * Whether the signed-in user may disable a given staff account.
 *
 * Mirrors the create rules: superAdmin is never touched from the UI, admin only by a superAdmin.
 * Self-disabling is refused outright — it would lock the operator out of this screen.
 */
export function canDisableStaffAccount(params: {
  targetId?: string;
  targetRole?: string;
  currentUserId?: string;
  currentUserIsSuperAdmin: boolean;
}): DisableEligibility {
  const { targetId, targetRole, currentUserId, currentUserIsSuperAdmin } = params;

  if (targetId && currentUserId && targetId === currentUserId) {
    return { allowed: false, reason: 'You cannot disable your own access.' };
  }
  if (targetRole === DISABLED_ROLE) {
    return { allowed: false, reason: 'This account already has no staff access.' };
  }
  if (targetRole === UserRole.SUPER_ADMIN) {
    return { allowed: false, reason: 'Super admin access cannot be disabled from here.' };
  }
  if (targetRole === UserRole.ADMIN && !currentUserIsSuperAdmin) {
    return { allowed: false, reason: 'Only a super admin can disable an admin.' };
  }
  return { allowed: true };
}
